import LocaleService from './LocaleService';
import MAP_DATA from '../../_json/_map_data.json';
import { COORDINATES_MULTIPLIER, MAP_MIN_ZOOM, LANGS } from '../map/MapConstants';

class MapDataService
{
    static getCoords(x, y, rc, multiplier = COORDINATES_MULTIPLIER)
    {
        return rc.unproject([x * multiplier, y * multiplier]);
    }

    // Get pin data with tooltip texts using new locale system
    static async getPinData(dataType, rc, lang, text_key = 'name', additional_keys = [])
    {
        const structuralData = MAP_DATA[dataType] || [];
        const results = [];

        for (const item of structuralData) 
        {
            const item2 = {};
            item2.id = item.id;
            item2.latlng = this.getCoords(item.x, item.y, rc);

            // Get localized data for all languages
            const texts = {};
            for (const langObj of LANGS) {
                const localizedItem = LocaleService.getItemData(langObj.code, dataType, item.id);
                const tooltipText = localizedItem?.[text_key] || localizedItem?.name || item.name || '';
                texts[langObj.code] = tooltipText;
            }
            
            item2.texts = texts;
            item2.tt_direction = item.tt_direction || 'right';
            item2.tt_min_zoom = item.tt_min_zoom || MAP_MIN_ZOOM;

            // Copy additional keys from structural data
            additional_keys.forEach((key) =>
            {
                item2[key] = item[key];
            });

            results.push(item2);
        }

        return results;
    }

    // Get pin data without tooltip texts
    static getPinData2(array, rc, get_texts = false, additional_keys = [])
    {
        const results = [];

        array.forEach((item, index) =>
        {
            const item2 = {};

            if (item.id) item2.id = item.id;
            if (item.key) item2.key = item.key;

            item2.latlng = this.getCoords(item.x, item.y, rc);

            if (get_texts)
            {
                const texts = {};
                // Note: This method needs to be updated when we migrate the components that use it
                texts[item.key] = item.name || '';
                item2.texts = texts;
            }

            additional_keys.forEach((key) =>
            {
                item2[key] = item[key];
            });

            results.push(item2);
        });

        return results;
    }

    static parseSymbolsData(data, rc)
    {
        // Mapping of boolean property names to symbol IDs
        const facilityMapping = {
            'info': 1,
            'wc': 2,
            'bf': 3,
            'ostomate': 4,
            'babybed': 5,
            'nursing': 6,
            'aed': 7,
            'elevator': 8,
            'phone': 9,
            'cafe': 10,
            'restaurant': 11,
            'wifi': 12,
            'restarea': 13,
            'smoking': 14,
            'restricted': 15,
            'backyard': 16
        };

        // Convert array of facility objects to the expected format
        const processedData = [];
        
        data.forEach(item => {
            // Extract facility IDs from boolean properties
            const ids = [];
            Object.keys(facilityMapping).forEach(facilityKey => {
                if (item[facilityKey] === true) {
                    ids.push({
                        id: facilityMapping[facilityKey],
                        minZoom: item.min_zoom || 2
                    });
                }
            });

            // Only add if there are facilities to show
            if (ids.length > 0) {
                processedData.push({
                    key: item.key,
                    ids: ids,
                    latlng: this.getCoords(item.x, item.y, rc),
                    colnum: item.colnum || 3,
                    group: item.group,
                    parent: item.parent
                });
            }
        });

        // Process parent-child relationships (same as before)
        processedData.forEach(symbol => {
            if (symbol.parent) {
                const parentSymbol = processedData.find(s => s.key === symbol.parent);
                const parentItem = data.find(d => d.key === symbol.parent);

                if (parentSymbol && parentItem) {
                    symbol.ids.forEach(childId => {
                        parentSymbol.ids.push({
                            id: childId.id,
                            minZoom: parentItem.min_zoom || 2,
                            maxZoom: (symbol.ids[0]?.minZoom || 2) - 1
                        });
                    });
                }
            }
        });

        return processedData;
    }

    /**
     * Merge simulation data from _map_data.json and locale files
     * @param {number} itemId - Building or Exterior ID to find simulation for
     * @param {string} lang - Language code
     * @param {string} type - Type of item ('building' or 'exterior')
     * @returns {Object|null} Merged simulation data (excludes structural properties like id, bid)
     */
    static getSimulationData(itemId, lang, type = 'building')
    {
        // Find simulation in _map_data.json by item ID and type
        const structuralSimulation = MAP_DATA.simulations?.find(sim => {
            if (type === 'exterior') {
                return sim.bid === itemId && sim['is-exterior'] === true;
            } else {
                return sim.bid === itemId && !sim['is-exterior'];
            }
        });
        
        if (!structuralSimulation) return null;

        // Get localized simulation data
        const localizedSimulation = LocaleService.getItemData(lang, 'simulations', structuralSimulation.id);
        if (!localizedSimulation) return null;

        // Start with localized data (descriptions, names, etc.)
        const mergedData = { ...localizedSimulation };

        // Map the structural data keys to the expected format
        // NOTE: We exclude structural properties like 'id', 'bid', 'is-exterior' 
        // to prevent them from overriding the original building/exterior data
        for (let i = 1; i <= 3; i++)
        {
            // Map img1, img2, img3 to sim_img1, sim_img2, sim_img3
            if (structuralSimulation[`img${i}`])
            {
                mergedData[`sim_img${i}`] = structuralSimulation[`img${i}`];
            }
            
            // Map audio1, audio2, audio3 to sim_audio1, sim_audio2, sim_audio3
            if (structuralSimulation[`audio${i}`])
            {
                mergedData[`sim_audio${i}`] = structuralSimulation[`audio${i}`];
            }
            
            // Map description1, description2, description3 to sim_description1, sim_description2, sim_description3
            if (localizedSimulation[`description${i}`])
            {
                mergedData[`sim_description${i}`] = localizedSimulation[`description${i}`];
            }
        }

        return mergedData;
    }

    /**
     * Get unified building data (structural + localized + simulation)
     * @param {number} id - Building ID
     * @param {string} lang - Language code
     * @returns {Object|null} Combined building data
     */
    static async getBuildingData(id, lang)
    {
        const structuralData = MAP_DATA.buildings?.find(item => item.id === id);
        if (!structuralData) return null;

        // Use synchronous method since locale data should be preloaded
        const localizedData = LocaleService.getItemData(lang, 'buildings', id);
        
        const item = { ...structuralData, ...localizedData };
        item.img = `${process.env.PUBLIC_URL}/img/tatemono/tatemono_pic_${item.zone}_${String(item.zone_site_id).padStart(2, '0')}_1.jpg`;

        // Check for simulation data and merge it
        const simulationData = this.getSimulationData(id, lang, 'building');
        if (simulationData)
        {
            item['has-simulation'] = true;
            
            // Only merge simulation-specific properties, not structural data like id, bid, etc.
            const simulationProps = {};
            Object.keys(simulationData).forEach(key => {
                // Only include simulation-specific properties (sim_* and description*)
                if (key.startsWith('sim_') || key.startsWith('description')) {
                    simulationProps[key] = simulationData[key];
                }
            });
            
            Object.assign(item, simulationProps);
        }

        return item;
    }

    /**
     * Get unified exterior data (structural + localized + simulation)
     * @param {number} id - Exterior ID
     * @param {string} lang - Language code
     * @returns {Object|null} Combined exterior data
     */
    static async getExteriorData(id, lang)
    {
        const structuralData = MAP_DATA.exteriors?.find(item => item.id === id);
        if (!structuralData) return null;

        // Use synchronous method since locale data should be preloaded
        const localizedData = LocaleService.getItemData(lang, 'exteriors', id);
        
        const item = { ...structuralData, ...localizedData };
        item.img = `${process.env.PUBLIC_URL}/img/exterior/exterior_pic_${String(item.id).padStart(2, '0')}_1.jpg`;

        // Check for simulation data and merge it (exteriors can have simulations too)
        const simulationData = this.getSimulationData(id, lang, 'exterior');
        if (simulationData)
        {
            item['has-simulation'] = true;
            
            // Only merge simulation-specific properties, not structural data like id, bid, etc.
            const simulationProps = {};
            Object.keys(simulationData).forEach(key => {
                // Only include simulation-specific properties (sim_* and description*)
                if (key.startsWith('sim_') || key.startsWith('description')) {
                    simulationProps[key] = simulationData[key];
                }
            });
            
            Object.assign(item, simulationProps);
        }

        return item;
    }

    /**
     * Get unified restaurant data (structural + localized)
     * @param {number} id - Restaurant ID
     * @param {string} lang - Language code
     * @returns {Object|null} Combined restaurant data
     */
    static async getRestaurantData(id, lang)
    {
        const structuralData = MAP_DATA.restaurants?.find(item => item.id === id);
        if (!structuralData) return null;

        // Use synchronous method since locale data should be preloaded
        const localizedData = LocaleService.getItemData(lang, 'restaurants', id);
        
        const item = { ...structuralData, ...localizedData };
        item.img = `${process.env.PUBLIC_URL}/img/restaurant/restaurant_pic_${String(id).padStart(2, '0')}_1.jpg`;

        return item;
    }

    // Legacy methods for backward compatibility (deprecated)
    static getSiteData(id)
    {
        console.warn('getSiteData is deprecated. Use getBuildingData instead.');
        const structuralData = MAP_DATA.buildings?.find(item => item.id === id);
        if (!structuralData) return null;

        const item = { ...structuralData };
        item.img = `${process.env.PUBLIC_URL}/img/tatemono/tatemono_pic_${item.zone}_${String(item.zone_site_id).padStart(2, '0')}_1.jpg`;

        return item;
    }

    static getExteriorDataLegacy(id)
    {
        console.warn('getExteriorData is deprecated. Use getExteriorData with lang parameter instead.');
        const structuralData = MAP_DATA.exteriors?.find(item => item.id === id);
        if (!structuralData) return null;

        const item = { ...structuralData };
        item.img = `${process.env.PUBLIC_URL}/img/exterior/exterior_pic_${String(item.id).padStart(2, '0')}_1.jpg`;

        return item;
    }

    static getFoodData(id)
    {
        console.warn('getFoodData is deprecated. Use getRestaurantData instead.');
        const structuralData = MAP_DATA.restaurants?.find(item => item.id === id);
        if (!structuralData) return null;

        const item = { ...structuralData };
        item.img = `${process.env.PUBLIC_URL}/img/restaurant/restaurant_pic_${String(id).padStart(2, '0')}_1.jpg`;

        return item;
    }
}

export default MapDataService; 